import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Clock, ArrowRight, Home } from "lucide-react";
import api from "@/lib/api";
import { LOGO } from "@/components/site/assets";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0; let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { if (active) setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { if (active) setState("error"); return; }
      } catch (err) {
        if (err.response?.status === 404) { if (active) setState("error"); return; }
        /* transient network error — retry */
      }
      tries += 1;
      if (tries >= 15) { if (active) setState("pending"); return; }
      if (active) setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md text-center">
        <img src={LOGO} alt="JJ Speed Factory" className="h-10 w-auto object-contain mx-auto mb-8" />
        {state === "checking" && (
          <div data-testid="payment-checking"><Loader2 size={40} className="mx-auto animate-spin text-white/60 mb-6" /><p className="text-white/60">Confirming your payment…</p></div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 size={48} className="mx-auto text-white mb-6" />
            <h1 className="font-display font-black uppercase tracking-tighter text-3xl mb-4">Payment Confirmed</h1>
            <p className="text-white/60 mb-6">Thank you. Check your email to activate your account and set your password. Your Origin Speed Academy access is ready once activated.</p>
            <a href="/" className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-white/85 transition-colors" data-testid="payment-home-btn">Back Home <ArrowRight size={16} /></a>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <Clock size={44} className="mx-auto text-white/60 mb-6" />
            <h1 className="font-display font-black uppercase tracking-tighter text-2xl mb-4">Payment Processing</h1>
            <p className="text-white/60 mb-6">Your payment is being finalized. You'll receive an activation email shortly. You can safely close this page.</p>
            <a href="/" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><Home size={14} /> Home</a>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <h1 className="font-display font-black uppercase tracking-tighter text-2xl mb-4">Something Went Wrong</h1>
            <p className="text-white/60 mb-6">We couldn't confirm this payment. If you were charged, contact us and we'll sort it out.</p>
            <a href="/online-coaching" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors">Try Again</a>
          </div>
        )}
      </div>
    </div>
  );
}
